/**
 * Aba Exceções (decisões 2 e 9 do Igor): o PCP marca, na aprovação do
 * executivo, só o ambiente que foge do comum — leve (metade de um ambiente),
 * pesado (o dobro, com o motivo) ou normal (revisado, sem exceção).
 *
 * "A revisar" = aprovados e em produção ainda sem marca, pela entrega. A
 * sugestão "leve" (banheiro, lavabo, hall, área de serviço) não grava nada:
 * só ajuda a selecionar. Ambiente com identidade duvidosa na planilha (pedido
 * em dois clientes, nome repetido no pedido) fica travado com o motivo, como
 * no histórico de comentários: a marca cairia no ambiente errado.
 *
 * Só aparece para quem tem `capacidade_fabrica:marcar_excecao`; quem só vê a
 * página lê a marca no painel do ambiente.
 */
import React, { useCallback, useMemo, useState } from "react";

import AsyncButton from "../../components/AsyncButton";
import Checkbox from "../../components/Checkbox";
import { useDados } from "../../context/DadosContext";
import {
  CLASSE,
  CLASSE_QUE_EXIGE_MOTIVO,
  ambientesMarcados,
  erroDaMarcacao,
  filaARevisar,
} from "../../utils/capacidade";
import { plural } from "../../utils/dinheiro";
import { bloqueioDoComentario, indiceDeIdentidade } from "../../utils/identidadeAmbiente";
import { chaveAmbiente, rotuloAmbiente } from "../MapaProducao/dados/chaves";
import {
  CLASSES_DA_MARCA,
  MOTIVOS_DA_MARCA,
  subtituloDoAmbiente,
  sugestaoDoAmbiente,
  textoDaMarca,
  textoDoSucesso,
  textoQuemQuando,
} from "./apresentacaoExcecoes";
import { Aviso, Bloco, BotaoTexto, LinkAmbiente, Regua, Segmentado, Selo, Vazio } from "./styles";
import {
  AcoesDoLote,
  BarraDoLote,
  CabecalhoLista,
  CamposDoLote,
  LinhaExcecao,
  ListaExcecoes,
} from "./stylesExcecoes";

const TODAS = "";
const FILTROS = [{ id: TODAS, rotulo: "Todos" }, ...CLASSES_DA_MARCA.map((c) => ({ id: c.id, rotulo: c.rotulo }))];
const TAMANHO_OBSERVACAO = 300;

/** Cada ambiente com a linha da planilha, o bloqueio e a sugestão. */
function enriquecer(itens, linhaDaPlanilha, indice) {
  return itens.map((item) => {
    const row = linhaDaPlanilha.get(item.chave) || null;
    const bloqueio = row ? bloqueioDoComentario(row, indice) : null;
    return {
      item,
      row,
      bloqueio: row ? bloqueio : { motivo: "sem_linha", mensagem: "Linha não encontrada na planilha atual." },
      sugestao: sugestaoDoAmbiente(item, row),
      rotulo: row ? rotuloAmbiente(row) : [item.pedido, item.ambiente].filter(Boolean).join(" · "),
    };
  });
}

function LinhaAmbiente({ linha, selecionado, onAlternar, podeAbrir, onAbrir, direita }) {
  const { item, bloqueio, rotulo } = linha;
  const abrivel = Boolean(podeAbrir?.(item));
  return (
    <LinhaExcecao data-chave={item.chave}>
      <Checkbox
        checked={selecionado}
        disabled={Boolean(bloqueio)}
        onChange={() => onAlternar(item.chave)}
        aria-label={`Selecionar ${rotulo}`}
        title={bloqueio ? bloqueio.mensagem : undefined}
      />
      <span className="texto">
        <LinkAmbiente
          type="button"
          className="nome"
          disabled={!abrivel}
          title={abrivel ? "Abrir o painel do ambiente" : rotulo}
          onClick={() => onAbrir(item)}
        >
          {rotulo || "—"}
        </LinkAmbiente>
        <span className="sub">{subtituloDoAmbiente(item)}</span>
        {bloqueio ? <span className="bloqueio">{bloqueio.mensagem}</span> : null}
        {item.marca?.observacao ? <span className="obs">{item.marca.observacao}</span> : null}
      </span>
      {direita}
    </LinhaExcecao>
  );
}

function BarraDeMarcacao({ quantidade, onMarcar, onLimpar }) {
  const [classe, setClasse] = useState(CLASSE.LEVE);
  const [motivo, setMotivo] = useState("");
  const [observacao, setObservacao] = useState("");
  const [erroServidor, setErroServidor] = useState(null);
  const erro = erroDaMarcacao({ quantidade, classe, motivo: motivo || null });
  const exigeMotivo = classe === CLASSE_QUE_EXIGE_MOTIVO;

  const marcar = async () => {
    setErroServidor(null);
    try {
      await onMarcar({ classe, motivo: exigeMotivo ? motivo : null, observacao: observacao.trim() });
      setObservacao("");
    } catch (falha) {
      setErroServidor(falha?.message || "Não foi possível marcar. Tente de novo.");
      throw falha;
    }
  };

  return (
    <BarraDoLote aria-label="Marcar os selecionados">
      <span className="resumo">{plural(quantidade, "ambiente selecionado", "ambientes selecionados")}</span>
      <Segmentado role="group" aria-label="Carga na marcenaria">
        {CLASSES_DA_MARCA.map((c) => (
          <button
            key={c.id}
            type="button"
            aria-pressed={classe === c.id}
            title={c.explicacao}
            onClick={() => setClasse(c.id)}
          >
            {c.rotulo}
          </button>
        ))}
      </Segmentado>
      <CamposDoLote>
        {exigeMotivo ? (
          <label>
            Por que é pesado
            <select value={motivo} onChange={(e) => setMotivo(e.target.value)} required aria-required="true">
              <option value="">Escolha o motivo</option>
              {MOTIVOS_DA_MARCA.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.rotulo}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label>
          Observação (opcional)
          <input
            type="text"
            value={observacao}
            maxLength={TAMANHO_OBSERVACAO}
            onChange={(e) => setObservacao(e.target.value)}
            placeholder={exigeMotivo ? "Ex.: ilha com curva no tampo" : ""}
          />
        </label>
      </CamposDoLote>
      {erroServidor ? (
        <p className="erro" role="alert">
          {erroServidor}
        </p>
      ) : null}
      {!erroServidor && erro && quantidade > 0 ? <p className="erro">{erro}</p> : null}
      <AcoesDoLote>
        <AsyncButton
          onClick={marcar}
          disabled={Boolean(erro)}
          successMessage={textoDoSucesso(quantidade, classe)}
          silentError
        >
          Marcar como {classe}
        </AsyncButton>
        <BotaoTexto type="button" onClick={onLimpar}>
          Limpar seleção
        </BotaoTexto>
      </AcoesDoLote>
    </BarraDoLote>
  );
}

function ErroDasMarcas({ erro, onTentarDeNovo }) {
  return (
    <Aviso $tom="warning" role="note">
      <div>
        <strong>Não foi possível ler as marcas do PCP.</strong> {erro} A conta segue sem os pesos.{" "}
        <BotaoTexto type="button" onClick={onTentarDeNovo} style={{ minHeight: 0, padding: "0 4px" }}>
          Tentar de novo
        </BotaoTexto>
      </div>
    </Aviso>
  );
}

/**
 * `painel` (abertos e linhas classificadas), `excecoes` = `useExcecoesCapacidade()`,
 * `linhaDaPlanilha` (Map chave → linha crua), `podeAbrir`/`onAbrir` (painel do ambiente).
 */
export default function AbaExcecoes({ painel, excecoes, linhaDaPlanilha, podeAbrir, onAbrir }) {
  const { dados } = useDados() || {};
  const { excecoes: marcas, marcar, carregado, erro, recarregar } = excecoes;
  const [selecionados, setSelecionados] = useState(() => new Set());
  const [filtro, setFiltro] = useState(TODAS);

  const indice = useMemo(() => (Array.isArray(dados) ? indiceDeIdentidade(dados) : null), [dados]);
  const aRevisar = useMemo(
    () => enriquecer(filaARevisar({ abertos: painel.abertos, marcas }), linhaDaPlanilha, indice),
    [painel.abertos, marcas, linhaDaPlanilha, indice],
  );
  const marcados = useMemo(
    () =>
      enriquecer(
        ambientesMarcados({ linhas: painel.classificacao?.linhas, marcas, classe: filtro || null }),
        linhaDaPlanilha,
        indice,
      ),
    [painel.classificacao, marcas, filtro, linhaDaPlanilha, indice],
  );
  const sugeridos = aRevisar.filter((l) => l.sugestao === CLASSE.LEVE && !l.bloqueio);
  const livres = aRevisar.filter((l) => !l.bloqueio);

  const alternar = useCallback((chave) => {
    setSelecionados((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(chave)) proximo.delete(chave);
      else proximo.add(chave);
      return proximo;
    });
  }, []);
  const selecionar = (linhas) => setSelecionados(new Set(linhas.map((l) => l.item.chave)));
  const limpar = () => setSelecionados(new Set());

  const marcarSelecionados = async (dadosDaMarca) => {
    // A chave e o rótulo saem da linha da planilha (a mesma régua do histórico
    // de comentários e do projetista); linha travada nunca entra no lote.
    const itens = [...aRevisar, ...marcados]
      .filter((l) => selecionados.has(l.item.chave) && l.row && !l.bloqueio)
      .map((l) => ({ chave: chaveAmbiente(l.row), rotulo: l.rotulo }));
    const unicos = [...new Map(itens.map((i) => [i.chave, i])).values()];
    await marcar(unicos, dadosDaMarca);
    limpar();
  };

  if (!carregado) return <Vazio>Lendo as marcas do PCP…</Vazio>;
  // Sem as marcas, "A revisar" traria todo mundo e "Marcados" diria que
  // ninguém marcou nada: o PCP remarcaria tudo. Melhor não mostrar as listas.
  if (erro) return <ErroDasMarcas erro={erro} onTentarDeNovo={recarregar} />;

  return (
    <>
      <Regua>
        Marque só o que foge do comum: leve vale meio ambiente, pesado vale dois (diga o motivo), normal diz que o
        ambiente foi revisado e não tem exceção. A conta oficial continua contando ambientes até as marcas cobrirem
        90% dos terminados usados no ritmo; até lá a conta com os pesos aparece como segunda leitura. A planilha não
        muda.
      </Regua>

      {selecionados.size > 0 ? (
        <BarraDeMarcacao quantidade={selecionados.size} onMarcar={marcarSelecionados} onLimpar={limpar} />
      ) : null}

      <Bloco aria-label="A revisar">
        <CabecalhoLista>
          <h2>
            A revisar <span>{aRevisar.length}</span>
          </h2>
          <span>
            {sugeridos.length > 0 ? (
              <BotaoTexto type="button" onClick={() => selecionar(sugeridos)}>
                {sugeridos.length === 1
                  ? "Selecionar a sugestão de leve"
                  : `Selecionar as ${sugeridos.length} sugestões de leve`}
              </BotaoTexto>
            ) : null}
            {livres.length > 0 ? (
              <BotaoTexto type="button" onClick={() => selecionar(livres)}>
                Selecionar todos
              </BotaoTexto>
            ) : null}
          </span>
        </CabecalhoLista>
        {aRevisar.length === 0 ? (
          <Vazio>Nenhum aprovado ou em produção sem marca.</Vazio>
        ) : (
          <ListaExcecoes>
            {aRevisar.map((linha) => (
              <LinhaAmbiente
                key={linha.item.chave}
                linha={linha}
                selecionado={selecionados.has(linha.item.chave)}
                onAlternar={alternar}
                podeAbrir={podeAbrir}
                onAbrir={onAbrir}
                direita={linha.sugestao ? <Selo>sugestão: {linha.sugestao}</Selo> : <span />}
              />
            ))}
          </ListaExcecoes>
        )}
      </Bloco>

      <Bloco aria-label="Marcados">
        <CabecalhoLista>
          <h2>
            Marcados <span>{marcados.length}</span>
          </h2>
          <Segmentado role="group" aria-label="Filtrar por carga">
            {FILTROS.map((f) => (
              <button key={f.id || "todos"} type="button" aria-pressed={filtro === f.id} onClick={() => setFiltro(f.id)}>
                {f.rotulo}
              </button>
            ))}
          </Segmentado>
        </CabecalhoLista>
        {marcados.length === 0 ? (
          <Vazio>{filtro ? "Nenhum ambiente com essa marca." : "Nenhum ambiente marcado ainda."}</Vazio>
        ) : (
          <ListaExcecoes>
            {marcados.map((linha) => (
              <LinhaAmbiente
                key={linha.item.chave}
                linha={linha}
                selecionado={selecionados.has(linha.item.chave)}
                onAlternar={alternar}
                podeAbrir={podeAbrir}
                onAbrir={onAbrir}
                direita={
                  <span className="marca" title={textoQuemQuando(linha.item.marca)}>
                    {textoDaMarca(linha.item.marca)}
                  </span>
                }
              />
            ))}
          </ListaExcecoes>
        )}
      </Bloco>
    </>
  );
}
